import React, { useContext } from 'react';
import { FormStepDefinition, SurveyDefinition, SurveyStepDefinition } from '../../../types/Survey';
import { ManagedResultsLookup } from '../../../helpers/surveyPredicates';
import { StepResultModel } from '../../../types/SurveyResult';
import { getContentItemSettings, getFormStyle, getIconImageStyles, getTextStyles } from '../Util/surveyStyles';
import { getVisibleFormItems, buildStepResult, resolvePresentedDate, isContinueDisabled, clearResultForSkip } from '../Util/surveyStepUtils';
import { TranslateContext } from '../../../widgets/MyDataHelps/TranslateContext';
import { SurveyStepContext } from '../Util/surveyStepContext';
import { renderFormStepToHtml } from '../Components/FormStepToHtml/FormStepToHtml';
import { useFormItemResults } from '../Util/useFormItemResults';
import IconImage from '../Components/IconImage/IconImage';
import SurveyMarkdown from '../Components/SurveyMarkdown/SurveyMarkdown';
import SurveyForm from '../Components/SurveyForm/SurveyForm';
import SurveyStepStickyFooter from '../Components/SurveyStepStickyFooter/SurveyStepStickyFooter';

export interface FormStepProps {
    step: FormStepDefinition;
    surveyDefinition: SurveyDefinition;
    nextStep: SurveyStepDefinition | null;
    managedResults: ManagedResultsLookup;
    predicateExpressionEvaluator: (expression: string) => boolean;
    onContinue: (stepResult: StepResultModel) => void;
    onBack: () => void;
    onResultChange: (stepResult: StepResultModel) => void;
    presentedDate?: string;
}

export default function FormStep({
    step,
    surveyDefinition,
    nextStep,
    managedResults,
    predicateExpressionEvaluator,
    onContinue,
    onResultChange: onResultChangeProp,
    presentedDate: presentedDateProp,
}: FormStepProps) {
    const translate = useContext(TranslateContext);
    const { mode } = useContext(SurveyStepContext) ?? { mode: 'Survey' as const };
    const presentedDate = resolvePresentedDate(presentedDateProp);
    const last = nextStep == null;

    // In Design mode, show all form items regardless of any hidePredicate type
    const visibleFormItems = getVisibleFormItems(step.formItems ?? [], managedResults, predicateExpressionEvaluator, mode === 'Design');

    const { results, visibleResults, handleResultChange } = useFormItemResults(
        step.identifier ?? '',
        step.formItems ?? [],
        managedResults,
        visibleFormItems,
        presentedDate,
        onResultChangeProp
    );

    const continueDisabled = isContinueDisabled(visibleFormItems, results);

    function handleContinue() {
        if (continueDisabled && step.optional === false) return;
        let stepResult = buildStepResult(step.identifier ?? '', presentedDate, visibleResults);
        if (step.type === 'ConsentFormStep') {
            stepResult = {
                ...stepResult,
                ConsentDocumentContent: renderFormStepToHtml(surveyDefinition, step, visibleResults, translate),
                ConsentDocumentTitle: step.title,
            };
        }
        onContinue(stepResult);
    }

    function handleSkip() {
        onContinue(buildStepResult(step.identifier ?? '', presentedDate, visibleResults.map(clearResultForSkip)));
    }

    const canSkip = step.optional === undefined || step.optional;

    return (
        <div className="form-step">
            <div className="current-step">
                <div className="step-content">
                    <IconImage iconImage={step.iconImage} iconImageStyles={getIconImageStyles(surveyDefinition, step)} />
                    {step.title && (
                        <div className="step-title" style={getTextStyles('title', surveyDefinition, step, true)}>
                            <SurveyMarkdown text={step.title} inline={true} />
                        </div>
                    )}
                    {step.text && (
                        <div className="step-text" style={getTextStyles('text', surveyDefinition, step, true)}>
                            <SurveyMarkdown text={step.text} />
                        </div>
                    )}
                    {step.image && (
                        <div className="step-image-main">
                            <img src={step.image} alt={step.imageAltText} />
                        </div>
                    )}
                    <SurveyForm
                        formItems={visibleFormItems}
                        results={visibleResults}
                        onResultChange={handleResultChange}
                        onEnterPressed={handleContinue}
                        variant={getFormStyle(surveyDefinition, step)}
                        contentItemSettings={getContentItemSettings(surveyDefinition, step)}
                    />
                </div>
            </div>
            <SurveyStepStickyFooter
                surveyDefinition={surveyDefinition}
                step={step}
                nextButtonText={step.nextButtonText}
                isLastStep={last}
                onContinue={handleContinue}
                nextDisabled={continueDisabled}
                canSkip={canSkip}
                onSkip={handleSkip}
            />
        </div>
    );
}
